import React from 'react';
import { LatestOrgRecords } from '@/components/org/LatestOrgRecords';
import { UpcomingOrgEvents } from '@/components/org/UpcomingOrgEvents';
import { useParams } from 'react-router';
import { OrgStatsComponent } from '@/components/org/OrgStatsComponent';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { MarkdownRender } from '@/components/ui/markdown-render';

interface Org {
  id: string;
  name: string;
  slug: string;
  telegramChatId: string | null;
  logoUrl: string | null;
  public: boolean;
  description: string | null;
}

export const OrgHomePage: React.FC = () => {
  const { orgId } = useParams();

  if (!orgId) return <div>Org ID not found</div>;

  const { data: org, isLoading, isError } = useQuery({
    queryKey: ['org', orgId],
    queryFn: async () => {
      const res = await fetch(`/api/org/${orgId}`);
      if (!res.ok) throw new Error('Failed to fetch organization');
      const org = await res.json() as Org;
      return org;
    },
  });

  if (isLoading) return <Loader2 className="h-8 w-8 animate-spin text-primary" />;
  if (isError) return <div className="bg-red-50 p-4 rounded-md text-red-800">Error loading organization</div>;

  return (

    <div className="w-full max-w-full overflow-hidden flex flex-col items-center px-2 sm:px-4 md:px-6">
      {/* Hero Section */}
      <section className="w-full max-w-full overflow-x-hidden md:max-w-6xl mb-8 md:mb-12">
        <div className="flex flex-col items-center md:items-start md:flex-row gap-4 md:gap-10">
          {/* Hero Text Section */}
          <div className="w-full max-w-full overflow-hidden md:flex-1 flex flex-col justify-center space-y-4 md:space-y-6 text-center md:text-left px-4">
            <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold tracking-tight text-primary mb-2 md:mb-4 break-words">{org?.name}</h1>

            {/* Stats with stronger overflow control */}
            <div className="my-3 w-full max-w-full overflow-hidden">
              <OrgStatsComponent orgId={orgId} />
            </div>

            {/* Text content with word breaking */}
            <MarkdownRender className="text-muted-foreground" content={org?.description || ''} />
          </div>

          {/* Latest Organisation Records */}
          <div className="w-full max-w-full overflow-hidden md:flex-1 flex flex-col min-h-[250px] md:min-h-[300px] mt-4 md:mt-0">
            <LatestOrgRecords orgId={orgId} />
          </div>
        </div>
      </section>

      {/* Records & Events Section */}
      <section className="w-full max-w-full overflow-hidden md:max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-10">
        <div className="p-1 sm:p-2 md:p-4 flex flex-col w-full max-w-full overflow-hidden min-h-[250px] md:min-h-[300px]">
          <h2 className="text-xl sm:text-2xl font-semibold mb-3 md:mb-4 break-words">Upcoming Organisation Events</h2>
          <UpcomingOrgEvents />
        </div>
      </section>
    </div>
  );
};

export default OrgHomePage;
